import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { 
  Settings, Palette, Building2, Save, MessageCircle, Share2, 
  CreditCard, Stethoscope, Plus, Edit2, Trash2, X, 
  Loader2, DollarSign, Clock, Check, Image as ImageIcon, MapPin, Upload
} from 'lucide-react';
import { useBranding } from '../../contexts/BrandingContext';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  'Injectables', 'Neurotoxins', 'PDO Threads', 'Lasers', 'Microneedling',
  'Chemical Peels', 'Hydrafacial', 'Facials', 'Body Treatments', 'Wellness', 'Beauty', 'Other'
];

const PRICE_UNITS = [
  'per treatment', 'per session', 'per unit', 'per syringe', 'per vial', 'per area', 'starting price', 'consultation'
];

const AppBuilder = () => {
  const { branding, updateBranding } = useBranding();
  const { toast } = useToast();
  
  // Settings state
  const [formData, setFormData] = useState(branding);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Locations state
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [savingLocation, setSavingLocation] = useState(false);
  const [locationFormData, setLocationFormData] = useState({
    name: '', address: '', city: '', state: '', zipCode: '', phone: '', email: '', isDefault: false, active: true
  });
  
  // Memberships state
  const [memberships, setMemberships] = useState([]);
  const [loadingMemberships, setLoadingMemberships] = useState(true);
  const [showMembershipForm, setShowMembershipForm] = useState(false);
  const [editingMembership, setEditingMembership] = useState(null);
  const [savingMembership, setSavingMembership] = useState(false);
  const [membershipFormData, setMembershipFormData] = useState({
    name: '', price: '', credits: '', benefits: '', popular: false, active: true, description: '', locationId: ''
  });
  
  // Treatments/Services state
  const [treatments, setTreatments] = useState([]);
  const [loadingTreatments, setLoadingTreatments] = useState(true);
  const [showTreatmentForm, setShowTreatmentForm] = useState(false);
  const [editingTreatment, setEditingTreatment] = useState(null);
  const [savingTreatment, setSavingTreatment] = useState(false);
  const [treatmentFormData, setTreatmentFormData] = useState({
    name: '', category: 'Injectables', price: '', priceUnit: 'per treatment',
    duration: '', description: '', image: '', skinConditions: '', popular: false, active: true
  });

  useEffect(() => {
    fetchLocations();
    fetchMemberships();
    fetchTreatments();
  }, []);

  // Locations API handlers
  const fetchLocations = async () => {
    try {
      setLoadingLocations(true);
      const response = await fetch(`${API}/locations?practiceId=demo-practice-001`);
      const data = await response.json();
      setLocations(data);
    } catch (err) {
      console.error('Error fetching locations:', err);
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleLocationSubmit = async (e) => {
    e.preventDefault();
    setSavingLocation(true);
    try {
      const data = { ...locationFormData, practiceId: 'demo-practice-001' };
      const url = editingLocation ? `${API}/locations/${editingLocation.id}` : `${API}/locations`;
      const method = editingLocation ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
      });

      if (!response.ok) throw new Error('Failed to save location');
      toast({ title: editingLocation ? 'Location Updated' : 'Location Added', description: `${locationFormData.name} has been saved.` });
      resetLocationForm();
      fetchLocations();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to save location.', variant: 'destructive' });
    } finally {
      setSavingLocation(false);
    }
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);
    setLocationFormData({
      name: location.name || '', address: location.address || '', city: location.city || '',
      state: location.state || '', zipCode: location.zipCode || '', phone: location.phone || '',
      email: location.email || '', isDefault: location.isDefault || false, active: location.active !== false
    });
    setShowLocationForm(true);
  };

  const handleDeleteLocation = async (location) => {
    if (!window.confirm(`Delete "${location.name}"?`)) return;
    try {
      await fetch(`${API}/locations/${location.id}`, { method: 'DELETE' });
      toast({ title: 'Deleted', description: `${location.name} has been deleted.` });
      fetchLocations();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete location.', variant: 'destructive' });
    }
  };

  const resetLocationForm = () => {
    setLocationFormData({ name: '', address: '', city: '', state: '', zipCode: '', phone: '', email: '', isDefault: false, active: true });
    setEditingLocation(null);
    setShowLocationForm(false);
  };

  // Settings handlers
  const handleSettingsChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    const result = await updateBranding(formData);
    setSavingSettings(false);
    
    if (result.success) {
      toast({ title: 'Settings Saved', description: 'Your practice settings have been updated successfully.' });
    } else {
      toast({ title: 'Error', description: 'Failed to save settings. Please try again.', variant: 'destructive' });
    }
  };

  // Memberships API handlers
  const fetchMemberships = async () => {
    try {
      setLoadingMemberships(true);
      const response = await fetch(`${API}/memberships?practiceId=demo-practice-001`);
      const data = await response.json();
      setMemberships(data);
    } catch (err) {
      console.error('Error fetching memberships:', err);
    } finally {
      setLoadingMemberships(false);
    }
  };

  const handleMembershipSubmit = async (e) => {
    e.preventDefault();
    setSavingMembership(true);
    try {
      const data = {
        ...membershipFormData,
        price: parseFloat(membershipFormData.price) || 0,
        credits: parseInt(membershipFormData.credits) || 0,
        benefits: membershipFormData.benefits.split('\n').map(b => b.trim()).filter(Boolean),
        practiceId: 'demo-practice-001'
      };

      const url = editingMembership ? `${API}/memberships/${editingMembership.id}` : `${API}/memberships`;
      const method = editingMembership ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
      });

      if (!response.ok) throw new Error('Failed to save membership');
      toast({ title: editingMembership ? 'Membership Updated' : 'Membership Added', description: `${membershipFormData.name} has been saved.` });
      resetMembershipForm();
      fetchMemberships();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to save membership.', variant: 'destructive' });
    } finally {
      setSavingMembership(false);
    }
  };

  const handleEditMembership = (membership) => {
    setEditingMembership(membership);
    setMembershipFormData({
      name: membership.name || '', price: membership.price?.toString() || '',
      credits: membership.credits?.toString() || '', benefits: (membership.benefits || []).join('\n'),
      popular: membership.popular || false, active: membership.active !== false, 
      description: membership.description || '', locationId: membership.locationId || ''
    });
    setShowMembershipForm(true);
  };

  const handleDeleteMembership = async (membership) => {
    if (!window.confirm(`Delete "${membership.name}"?`)) return;
    try {
      await fetch(`${API}/memberships/${membership.id}`, { method: 'DELETE' });
      toast({ title: 'Deleted', description: `${membership.name} has been deleted.` });
      fetchMemberships();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete membership.', variant: 'destructive' });
    }
  };

  const resetMembershipForm = () => {
    setMembershipFormData({ name: '', price: '', credits: '', benefits: '', popular: false, active: true, description: '', locationId: '' });
    setEditingMembership(null);
    setShowMembershipForm(false);
  };

  // Treatments API handlers
  const fetchTreatments = async () => {
    try {
      setLoadingTreatments(true);
      const response = await fetch(`${API}/treatments?practiceId=demo-practice-001`);
      const data = await response.json();
      setTreatments(data);
    } catch (err) {
      console.error('Error fetching treatments:', err);
    } finally {
      setLoadingTreatments(false);
    }
  };

  const handleTreatmentSubmit = async (e) => {
    e.preventDefault();
    setSavingTreatment(true);
    try {
      const data = {
        ...treatmentFormData,
        price: parseFloat(treatmentFormData.price) || 0,
        duration: parseInt(treatmentFormData.duration) || 30,
        skinConditions: treatmentFormData.skinConditions.split(',').map(s => s.trim()).filter(Boolean),
        practiceId: 'demo-practice-001'
      };

      const url = editingTreatment ? `${API}/treatments/${editingTreatment.id}` : `${API}/treatments`;
      const method = editingTreatment ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
      });

      if (!response.ok) throw new Error('Failed to save treatment');
      toast({ title: editingTreatment ? 'Service Updated' : 'Service Added', description: `${treatmentFormData.name} has been saved.` });
      resetTreatmentForm();
      fetchTreatments();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to save service.', variant: 'destructive' });
    } finally {
      setSavingTreatment(false);
    }
  };

  const handleEditTreatment = (treatment) => {
    setEditingTreatment(treatment);
    setTreatmentFormData({
      name: treatment.name || '', category: treatment.category || 'Injectables',
      price: treatment.price?.toString() || '', priceUnit: treatment.priceUnit || 'per treatment',
      duration: treatment.duration?.toString() || '', description: treatment.description || '',
      image: treatment.image || '', skinConditions: (treatment.skinConditions || []).join(', '),
      popular: treatment.popular || false, active: treatment.active !== false
    });
    setShowTreatmentForm(true);
  };

  const handleDeleteTreatment = async (treatment) => {
    if (!window.confirm(`Delete "${treatment.name}"?`)) return;
    try {
      await fetch(`${API}/treatments/${treatment.id}`, { method: 'DELETE' });
      toast({ title: 'Deleted', description: `${treatment.name} has been deleted.` });
      fetchTreatments();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete service.', variant: 'destructive' });
    }
  };

  const resetTreatmentForm = () => {
    setTreatmentFormData({
      name: '', category: 'Injectables', price: '', priceUnit: 'per treatment',
      duration: '', description: '', image: '', skinConditions: '', popular: false, active: true
    });
    setEditingTreatment(null);
    setShowTreatmentForm(false);
  };

  const getLocationName = (locationId) => {
    if (!locationId) return 'All Locations';
    const loc = locations.find(l => l.id === locationId);
    return loc ? loc.name : 'Unknown';
  };

  return (
    <div className="space-y-6" data-testid="app-builder">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">App Builder</h1>
          <p className="text-gray-600">Configure your app's branding, services, and memberships</p>
        </div>
      </div>

      <Tabs defaultValue="branding" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="branding" data-testid="tab-branding">
            <Palette className="mr-2 h-4 w-4" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="practice" data-testid="tab-practice">
            <Building2 className="mr-2 h-4 w-4" />
            Practice Info
          </TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations">
            <Settings className="mr-2 h-4 w-4" />
            Integrations
          </TabsTrigger>
          <TabsTrigger value="memberships" data-testid="tab-memberships">
            <CreditCard className="mr-2 h-4 w-4" />
            Memberships
          </TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-services">
            <Stethoscope className="mr-2 h-4 w-4" />
            Services
          </TabsTrigger>
        </TabsList>

        {/* Branding Tab */}
        <TabsContent value="branding" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Brand Identity</CardTitle>
                  <CardDescription>Customize how your practice appears to patients</CardDescription>
                </div>
                <Button onClick={handleSaveSettings} disabled={savingSettings}>
                  <Save className="mr-2 h-4 w-4" />
                  {savingSettings ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="practiceName">Practice Name</Label>
                <Input id="practiceName" value={formData.practiceName} onChange={(e) => handleSettingsChange('practiceName', e.target.value)} placeholder="Acme Med Spa" className="mt-2" />
              </div>
              
              {/* Logo Upload Section */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <Label className="text-base font-semibold">Logo</Label>
                <p className="text-sm text-gray-500 mb-3">Upload your practice logo (recommended size: 200x60px)</p>
                <div className="flex items-start gap-4">
                  <div className="w-48 h-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-white">
                    {formData.logoUrl ? (
                      <img src={formData.logoUrl} alt="Logo" className="max-h-14 max-w-44 object-contain" />
                    ) : (
                      <div className="text-center text-gray-400">
                        <Upload className="h-6 w-6 mx-auto mb-1" />
                        <span className="text-xs">No logo</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <Input value={formData.logoUrl || ''} onChange={(e) => handleSettingsChange('logoUrl', e.target.value)} placeholder="https://example.com/logo.png" />
                    <p className="text-xs text-gray-500 mt-1">Paste URL to your logo image</p>
                  </div>
                </div>
              </div>

              {/* Hero Image Section */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <Label className="text-base font-semibold">Hero Image</Label>
                <p className="text-sm text-gray-500 mb-3">Main banner image for your app home page</p>
                <div className="space-y-4">
                  <div className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-white overflow-hidden">
                    {formData.heroImageUrl ? (
                      <img src={formData.heroImageUrl} alt="Hero" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center text-gray-400">
                        <ImageIcon className="h-8 w-8 mx-auto mb-1" />
                        <span className="text-sm">No hero image</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="heroImageUrl">Hero Image URL</Label>
                    <Input id="heroImageUrl" value={formData.heroImageUrl || ''} onChange={(e) => handleSettingsChange('heroImageUrl', e.target.value)} placeholder="https://example.com/hero.jpg" className="mt-1" />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="heroHeader">Header Text <span className="text-gray-400">(40 characters)</span></Label>
                      <Input id="heroHeader" value={formData.heroHeader || ''} onChange={(e) => handleSettingsChange('heroHeader', e.target.value.slice(0, 40))} placeholder="Welcome to Our Spa" maxLength={40} className="mt-1" />
                      <p className="text-xs text-gray-500 mt-1">{(formData.heroHeader || '').length}/40</p>
                    </div>
                    <div>
                      <Label htmlFor="heroSubheader">Subheader Text <span className="text-gray-400">(90 characters)</span></Label>
                      <Input id="heroSubheader" value={formData.heroSubheader || ''} onChange={(e) => handleSettingsChange('heroSubheader', e.target.value.slice(0, 90))} placeholder="Experience premium treatments" maxLength={90} className="mt-1" />
                      <p className="text-xs text-gray-500 mt-1">{(formData.heroSubheader || '').length}/90</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="primaryColor">Primary Color</Label>
                  <div className="flex gap-2 mt-2">
                    <Input id="primaryColor" type="color" value={formData.primaryColor?.includes('hsl') ? '#DCC3B0' : formData.primaryColor} onChange={(e) => handleSettingsChange('primaryColor', e.target.value)} className="w-20 h-10" />
                    <Input value={formData.primaryColor} onChange={(e) => handleSettingsChange('primaryColor', e.target.value)} placeholder="#DCC3B0" className="flex-1" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="secondaryColor">Secondary Color</Label>
                  <div className="flex gap-2 mt-2">
                    <Input id="secondaryColor" type="color" value={formData.secondaryColor?.includes('hsl') ? '#F5F5F5' : formData.secondaryColor} onChange={(e) => handleSettingsChange('secondaryColor', e.target.value)} className="w-20 h-10" />
                    <Input value={formData.secondaryColor} onChange={(e) => handleSettingsChange('secondaryColor', e.target.value)} placeholder="#F5F5F5" className="flex-1" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="accentColor">Accent Color</Label>
                  <div className="flex gap-2 mt-2">
                    <Input id="accentColor" type="color" value={formData.accentColor?.includes('hsl') ? '#E8D5C4' : formData.accentColor} onChange={(e) => handleSettingsChange('accentColor', e.target.value)} className="w-20 h-10" />
                    <Input value={formData.accentColor} onChange={(e) => handleSettingsChange('accentColor', e.target.value)} placeholder="#E8D5C4" className="flex-1" />
                  </div>
                </div>
              </div>
              <div>
                <Label htmlFor="description">Practice Description</Label>
                <Textarea id="description" value={formData.description} onChange={(e) => handleSettingsChange('description', e.target.value)} placeholder="Premium aesthetic and wellness treatments" rows={3} className="mt-2" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Practice Info Tab */}
        <TabsContent value="practice" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Contact Information</CardTitle>
                  <CardDescription>Primary contact details for your practice</CardDescription>
                </div>
                <Button onClick={handleSaveSettings} disabled={savingSettings}>
                  <Save className="mr-2 h-4 w-4" />
                  {savingSettings ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contactEmail">Email Address</Label>
                  <Input id="contactEmail" type="email" value={formData.contactEmail} onChange={(e) => handleSettingsChange('contactEmail', e.target.value)} placeholder="hello@acmemedspa.com" className="mt-2" />
                </div>
                <div>
                  <Label htmlFor="contactPhone">Phone Number</Label>
                  <Input id="contactPhone" type="tel" value={formData.contactPhone} onChange={(e) => handleSettingsChange('contactPhone', e.target.value)} placeholder="(555) 123-4567" className="mt-2" />
                </div>
              </div>
              <div>
                <Label htmlFor="website">Website</Label>
                <Input id="website" type="url" value={formData.website} onChange={(e) => handleSettingsChange('website', e.target.value)} placeholder="https://acmemedspa.com" className="mt-2" />
              </div>
            </CardContent>
          </Card>

          {/* Locations Section */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2"><MapPin className="h-5 w-5" />Locations</CardTitle>
                  <CardDescription>Manage your spa locations (for multi-location practices)</CardDescription>
                </div>
                <Button onClick={() => setShowLocationForm(true)} data-testid="add-location-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Location
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingLocations ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : locations.length === 0 ? (
                <div className="text-center py-8">
                  <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No locations</h3>
                  <p className="text-gray-500 mb-4">Add your first spa location</p>
                  <Button onClick={() => setShowLocationForm(true)}>
                    <Plus className="h-4 w-4 mr-2" />Add Location
                  </Button>
                </div>
              ) : (
                <div className="divide-y">
                  {locations.map((location) => (
                    <div key={location.id} className="py-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <MapPin className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-gray-900">{location.name}</h4>
                            {location.isDefault && <Badge className="bg-green-100 text-green-800 text-xs">Default</Badge>}
                            {!location.active && <Badge variant="secondary" className="text-xs">Inactive</Badge>}
                          </div>
                          <p className="text-sm text-gray-500">
                            {[location.address, location.city, location.state, location.zipCode].filter(Boolean).join(', ') || 'No address'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleEditLocation(location)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteLocation(location)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="space-y-6">
          {/* AI Advisor Settings */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2"><MessageCircle className="h-5 w-5" />AI Advisor</CardTitle>
                  <CardDescription>Customize your AI beauty advisor's name and personality</CardDescription>
                </div>
                <Button onClick={handleSaveSettings} disabled={savingSettings}>
                  <Save className="mr-2 h-4 w-4" />
                  {savingSettings ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="chatbotName">Advisor Name</Label>
                  <Input id="chatbotName" value={formData.chatbotName || ''} onChange={(e) => handleSettingsChange('chatbotName', e.target.value)} placeholder="e.g., Elle, Aria, Sophie" className="mt-2" />
                  <p className="text-sm text-gray-500 mt-1">This name appears in the chat header and welcome message</p>
                </div>
              </div>
              <div>
                <Label htmlFor="chatbotWelcomeMessage">Welcome Message</Label>
                <Textarea id="chatbotWelcomeMessage" value={formData.chatbotWelcomeMessage || ''} onChange={(e) => handleSettingsChange('chatbotWelcomeMessage', e.target.value)} placeholder="Hi! I'm your AI Beauty Advisor. How can I help you today?" rows={3} className="mt-2" />
              </div>
            </CardContent>
          </Card>

          {/* Payment Processing */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5" />Payment Processing</CardTitle>
              <CardDescription>Configure your payment gateway credentials</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4 p-4 border rounded-lg">
                  <h4 className="font-semibold flex items-center gap-2">Stripe</h4>
                  <div>
                    <Label htmlFor="stripePublishableKey">Publishable Key</Label>
                    <Input id="stripePublishableKey" value={formData.stripePublishableKey || ''} onChange={(e) => handleSettingsChange('stripePublishableKey', e.target.value)} placeholder="pk_live_..." className="mt-1 font-mono text-sm" />
                  </div>
                  <div>
                    <Label htmlFor="stripeSecretKey">Secret Key</Label>
                    <Input id="stripeSecretKey" type="password" value={formData.stripeSecretKey || ''} onChange={(e) => handleSettingsChange('stripeSecretKey', e.target.value)} placeholder="sk_live_..." className="mt-1 font-mono text-sm" />
                  </div>
                </div>
                <div className="space-y-4 p-4 border rounded-lg">
                  <h4 className="font-semibold flex items-center gap-2">PayPal</h4>
                  <div>
                    <Label htmlFor="paypalClientId">Client ID</Label>
                    <Input id="paypalClientId" value={formData.paypalClientId || ''} onChange={(e) => handleSettingsChange('paypalClientId', e.target.value)} placeholder="Your PayPal Client ID" className="mt-1 font-mono text-sm" />
                  </div>
                  <div>
                    <Label htmlFor="paypalClientSecret">Client Secret</Label>
                    <Input id="paypalClientSecret" type="password" value={formData.paypalClientSecret || ''} onChange={(e) => handleSettingsChange('paypalClientSecret', e.target.value)} placeholder="Your PayPal Secret" className="mt-1 font-mono text-sm" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* SMS (Twilio) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><MessageCircle className="h-5 w-5" />SMS Integration (Twilio)</CardTitle>
              <CardDescription>Configure Twilio for automated text messages</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="twilioAccountSid">Account SID</Label>
                  <Input id="twilioAccountSid" value={formData.twilioAccountSid || ''} onChange={(e) => handleSettingsChange('twilioAccountSid', e.target.value)} placeholder="AC..." className="mt-1 font-mono text-sm" />
                </div>
                <div>
                  <Label htmlFor="twilioAuthToken">Auth Token</Label>
                  <Input id="twilioAuthToken" type="password" value={formData.twilioAuthToken || ''} onChange={(e) => handleSettingsChange('twilioAuthToken', e.target.value)} placeholder="Your auth token" className="mt-1 font-mono text-sm" />
                </div>
                <div>
                  <Label htmlFor="twilioPhoneNumber">Phone Number</Label>
                  <Input id="twilioPhoneNumber" value={formData.twilioPhoneNumber || ''} onChange={(e) => handleSettingsChange('twilioPhoneNumber', e.target.value)} placeholder="+1234567890" className="mt-1 font-mono text-sm" />
                </div>
              </div>
              <p className="text-sm text-gray-500">Get your credentials from <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="text-primary underline">Twilio Console</a></p>
            </CardContent>
          </Card>

          {/* Email (SendGrid) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Share2 className="h-5 w-5" />Email Integration (SendGrid)</CardTitle>
              <CardDescription>Configure SendGrid for automated emails</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="sendgridApiKey">API Key</Label>
                  <Input id="sendgridApiKey" type="password" value={formData.sendgridApiKey || ''} onChange={(e) => handleSettingsChange('sendgridApiKey', e.target.value)} placeholder="SG...." className="mt-1 font-mono text-sm" />
                </div>
                <div>
                  <Label htmlFor="sendgridFromEmail">From Email</Label>
                  <Input id="sendgridFromEmail" type="email" value={formData.sendgridFromEmail || ''} onChange={(e) => handleSettingsChange('sendgridFromEmail', e.target.value)} placeholder="noreply@yourspa.com" className="mt-1" />
                </div>
              </div>
              <div>
                <Label htmlFor="sendgridFromName">From Name</Label>
                <Input id="sendgridFromName" value={formData.sendgridFromName || ''} onChange={(e) => handleSettingsChange('sendgridFromName', e.target.value)} placeholder="Acme Med Spa" className="mt-1" />
              </div>
              <p className="text-sm text-gray-500">Get your API key from <a href="https://sendgrid.com" target="_blank" rel="noopener noreferrer" className="text-primary underline">SendGrid Dashboard</a></p>
            </CardContent>
          </Card>

          {/* Booking System */}
          <Card>
            <CardHeader>
              <CardTitle>Booking System</CardTitle>
              <CardDescription>Connect your online booking system</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="boulevardBusinessId">Boulevard Business ID</Label>
                <Input id="boulevardBusinessId" value={formData.boulevardBusinessId || ''} onChange={(e) => handleSettingsChange('boulevardBusinessId', e.target.value)} placeholder="e.g., 5e7e7c8d-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="mt-2 font-mono text-sm" />
                <p className="text-sm text-gray-500 mt-1">Find this in your Boulevard dashboard under Settings - Developer</p>
              </div>
              <div>
                <Label htmlFor="businessHours">Business Hours</Label>
                <Input id="businessHours" value={formData.businessHours || ''} onChange={(e) => handleSettingsChange('businessHours', e.target.value)} placeholder="e.g., Mon-Sat: 9am-7pm" className="mt-2" />
              </div>
            </CardContent>
          </Card>

          {/* Social Media Links */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Share2 className="h-5 w-5" />Social Media Links</CardTitle>
              <CardDescription>Connect your social profiles (shown in footer)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="instagramUrl">Instagram</Label>
                  <Input id="instagramUrl" type="url" value={formData.instagramUrl || ''} onChange={(e) => handleSettingsChange('instagramUrl', e.target.value)} placeholder="https://instagram.com/yourspa" className="mt-2" />
                </div>
                <div>
                  <Label htmlFor="facebookUrl">Facebook</Label>
                  <Input id="facebookUrl" type="url" value={formData.facebookUrl || ''} onChange={(e) => handleSettingsChange('facebookUrl', e.target.value)} placeholder="https://facebook.com/yourspa" className="mt-2" />
                </div>
                <div>
                  <Label htmlFor="tiktokUrl">TikTok</Label>
                  <Input id="tiktokUrl" type="url" value={formData.tiktokUrl || ''} onChange={(e) => handleSettingsChange('tiktokUrl', e.target.value)} placeholder="https://tiktok.com/@yourspa" className="mt-2" />
                </div>
                <div>
                  <Label htmlFor="youtubeUrl">YouTube</Label>
                  <Input id="youtubeUrl" type="url" value={formData.youtubeUrl || ''} onChange={(e) => handleSettingsChange('youtubeUrl', e.target.value)} placeholder="https://youtube.com/@yourspa" className="mt-2" />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Memberships Tab */}
        <TabsContent value="memberships" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Membership Plans</CardTitle>
                  <CardDescription>Create and manage membership tiers for your patients</CardDescription>
                </div>
                <Button onClick={() => setShowMembershipForm(true)} data-testid="add-membership-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Membership
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingMemberships ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : memberships.length === 0 ? (
                <div className="text-center py-12">
                  <CreditCard className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No membership plans</h3>
                  <p className="text-gray-500 mb-4">Create your first membership plan</p>
                  <Button onClick={() => setShowMembershipForm(true)}>
                    <Plus className="h-4 w-4 mr-2" />Add Membership
                  </Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {memberships.map((membership) => (
                    <Card key={membership.id} className={`relative ${membership.popular ? 'ring-2 ring-primary' : ''}`}>
                      {membership.popular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <Badge className="bg-primary">Most Popular</Badge>
                        </div>
                      )}
                      <CardHeader className="pb-2">
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                              {membership.name}
                              {!membership.active && <Badge variant="secondary" className="text-xs">Inactive</Badge>}
                            </CardTitle>
                            {membership.locationId && (
                              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {getLocationName(membership.locationId)}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEditMembership(membership)}>
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteMembership(membership)} className="text-red-500 hover:text-red-700">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="mb-3">
                          <span className="text-2xl font-bold">${membership.price}</span>
                          <span className="text-gray-500">/month</span>
                        </div>
                        {membership.credits > 0 && (
                          <p className="text-sm text-gray-600 mb-3">{membership.credits} credits included monthly</p>
                        )}
                        {membership.benefits?.length > 0 && (
                          <ul className="space-y-1">
                            {membership.benefits.slice(0, 3).map((benefit, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm">
                                <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                <span>{benefit}</span>
                              </li>
                            ))}
                            {membership.benefits.length > 3 && (
                              <li className="text-sm text-gray-500">+{membership.benefits.length - 3} more benefits</li>
                            )}
                          </ul>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Services Tab */}
        <TabsContent value="services" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Treatment Menu</CardTitle>
                  <CardDescription>Manage your spa's treatment offerings and pricing</CardDescription>
                </div>
                <Button onClick={() => setShowTreatmentForm(true)} data-testid="add-service-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Service
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingTreatments ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : treatments.length === 0 ? (
                <div className="text-center py-12">
                  <Stethoscope className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No services</h3>
                  <p className="text-gray-500 mb-4">Add your first treatment service</p>
                  <Button onClick={() => setShowTreatmentForm(true)}>
                    <Plus className="h-4 w-4 mr-2" />Add Service
                  </Button>
                </div>
              ) : (
                <div className="divide-y">
                  {treatments.map((treatment) => (
                    <div key={treatment.id} className="py-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {treatment.image ? (
                          <img src={treatment.image} alt={treatment.name} className="w-14 h-14 rounded-lg object-cover" />
                        ) : (
                          <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Stethoscope className="h-5 w-5 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-gray-900">{treatment.name}</h4>
                            {treatment.popular && <Badge className="bg-primary text-xs">Popular</Badge>}
                            {!treatment.active && <Badge variant="secondary" className="text-xs">Inactive</Badge>}
                          </div>
                          <p className="text-sm text-gray-500">{treatment.category}</p>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                            <span className="font-medium text-gray-900">${treatment.price} {treatment.priceUnit}</span>
                            {treatment.duration && (
                              <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{treatment.duration} min</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleEditTreatment(treatment)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteTreatment(treatment)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Location Form Modal */}
      {showLocationForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>{editingLocation ? 'Edit Location' : 'Add New Location'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={resetLocationForm}><X className="h-4 w-4" /></Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLocationSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="locName">Location Name *</Label>
                  <Input id="locName" value={locationFormData.name} onChange={(e) => setLocationFormData({...locationFormData, name: e.target.value})} placeholder="e.g., Beverly Hills" required className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="locAddress">Street Address</Label>
                  <Input id="locAddress" value={locationFormData.address} onChange={(e) => setLocationFormData({...locationFormData, address: e.target.value})} placeholder="123 Main St" className="mt-1" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="locCity">City</Label>
                    <Input id="locCity" value={locationFormData.city} onChange={(e) => setLocationFormData({...locationFormData, city: e.target.value})} placeholder="Los Angeles" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="locState">State</Label>
                    <Input id="locState" value={locationFormData.state} onChange={(e) => setLocationFormData({...locationFormData, state: e.target.value})} placeholder="CA" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="locZip">Zip Code</Label>
                    <Input id="locZip" value={locationFormData.zipCode} onChange={(e) => setLocationFormData({...locationFormData, zipCode: e.target.value})} placeholder="90210" className="mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="locPhone">Phone</Label>
                    <Input id="locPhone" type="tel" value={locationFormData.phone} onChange={(e) => setLocationFormData({...locationFormData, phone: e.target.value})} placeholder="(555) 123-4567" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="locEmail">Email</Label>
                    <Input id="locEmail" type="email" value={locationFormData.email} onChange={(e) => setLocationFormData({...locationFormData, email: e.target.value})} placeholder="location@spa.com" className="mt-1" />
                  </div>
                </div>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={locationFormData.isDefault} onChange={(e) => setLocationFormData({...locationFormData, isDefault: e.target.checked})} className="rounded" />
                    <span className="text-sm">Set as Default</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={locationFormData.active} onChange={(e) => setLocationFormData({...locationFormData, active: e.target.checked})} className="rounded" />
                    <span className="text-sm">Active</span>
                  </label>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetLocationForm}>Cancel</Button>
                  <Button type="submit" disabled={savingLocation}>
                    {savingLocation ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><Save className="h-4 w-4 mr-2" />{editingLocation ? 'Update' : 'Add'} Location</>}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Membership Form Modal */}
      {showMembershipForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>{editingMembership ? 'Edit Membership' : 'Add New Membership'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={resetMembershipForm}><X className="h-4 w-4" /></Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleMembershipSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="mName">Plan Name *</Label>
                  <Input id="mName" value={membershipFormData.name} onChange={(e) => setMembershipFormData({...membershipFormData, name: e.target.value})} placeholder="e.g., Gold Membership" required className="mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="mPrice">Monthly Price *</Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input id="mPrice" type="number" value={membershipFormData.price} onChange={(e) => setMembershipFormData({...membershipFormData, price: e.target.value})} placeholder="99" required className="pl-10" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="mCredits">Monthly Credits</Label>
                    <Input id="mCredits" type="number" value={membershipFormData.credits} onChange={(e) => setMembershipFormData({...membershipFormData, credits: e.target.value})} placeholder="100" className="mt-1" />
                  </div>
                </div>
                {/* Location Dropdown */}
                <div>
                  <Label htmlFor="mLocation">Location</Label>
                  <select id="mLocation" value={membershipFormData.locationId} onChange={(e) => setMembershipFormData({...membershipFormData, locationId: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                    <option value="">All Locations</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Select a specific location or leave as "All Locations"</p>
                </div>
                <div>
                  <Label htmlFor="mDesc">Description</Label>
                  <Input id="mDesc" value={membershipFormData.description} onChange={(e) => setMembershipFormData({...membershipFormData, description: e.target.value})} placeholder="Brief description" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="mBenefits">Benefits (one per line)</Label>
                  <Textarea id="mBenefits" value={membershipFormData.benefits} onChange={(e) => setMembershipFormData({...membershipFormData, benefits: e.target.value})} placeholder="Priority booking&#10;10% off all treatments&#10;Free monthly facial" rows={4} className="mt-1" />
                </div>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={membershipFormData.popular} onChange={(e) => setMembershipFormData({...membershipFormData, popular: e.target.checked})} className="rounded" />
                    <span className="text-sm">Mark as Popular</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={membershipFormData.active} onChange={(e) => setMembershipFormData({...membershipFormData, active: e.target.checked})} className="rounded" />
                    <span className="text-sm">Active</span>
                  </label>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetMembershipForm}>Cancel</Button>
                  <Button type="submit" disabled={savingMembership}>
                    {savingMembership ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><Save className="h-4 w-4 mr-2" />{editingMembership ? 'Update' : 'Add'} Plan</>}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Treatment Form Modal */}
      {showTreatmentForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>{editingTreatment ? 'Edit Service' : 'Add New Service'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={resetTreatmentForm}><X className="h-4 w-4" /></Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleTreatmentSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="tName">Service Name *</Label>
                    <Input id="tName" value={treatmentFormData.name} onChange={(e) => setTreatmentFormData({...treatmentFormData, name: e.target.value})} placeholder="e.g., Lip Filler" required className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="tCategory">Category *</Label>
                    <select id="tCategory" value={treatmentFormData.category} onChange={(e) => setTreatmentFormData({...treatmentFormData, category: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" required>
                      {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="tPrice">Price *</Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input id="tPrice" type="number" value={treatmentFormData.price} onChange={(e) => setTreatmentFormData({...treatmentFormData, price: e.target.value})} placeholder="0" required className="pl-10" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="tPriceUnit">Price Unit</Label>
                    <select id="tPriceUnit" value={treatmentFormData.priceUnit} onChange={(e) => setTreatmentFormData({...treatmentFormData, priceUnit: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                      {PRICE_UNITS.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="tDuration">Duration (mins)</Label>
                    <div className="relative mt-1">
                      <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input id="tDuration" type="number" value={treatmentFormData.duration} onChange={(e) => setTreatmentFormData({...treatmentFormData, duration: e.target.value})} placeholder="30" className="pl-10" />
                    </div>
                  </div>
                </div>
                <div>
                  <Label htmlFor="tDesc">Description</Label>
                  <Textarea id="tDesc" value={treatmentFormData.description} onChange={(e) => setTreatmentFormData({...treatmentFormData, description: e.target.value})} placeholder="Describe the treatment..." rows={3} className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="tImage">Image URL</Label>
                  <div className="relative mt-1">
                    <ImageIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input id="tImage" value={treatmentFormData.image} onChange={(e) => setTreatmentFormData({...treatmentFormData, image: e.target.value})} placeholder="https://example.com/image.jpg" className="pl-10" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="tConditions">Treats (comma-separated)</Label>
                  <Input id="tConditions" value={treatmentFormData.skinConditions} onChange={(e) => setTreatmentFormData({...treatmentFormData, skinConditions: e.target.value})} placeholder="e.g., wrinkles, fine-lines, volume-loss" className="mt-1" />
                </div>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={treatmentFormData.popular} onChange={(e) => setTreatmentFormData({...treatmentFormData, popular: e.target.checked})} className="rounded" />
                    <span className="text-sm">Mark as Popular</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={treatmentFormData.active} onChange={(e) => setTreatmentFormData({...treatmentFormData, active: e.target.checked})} className="rounded" />
                    <span className="text-sm">Active</span>
                  </label>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetTreatmentForm}>Cancel</Button>
                  <Button type="submit" disabled={savingTreatment}>
                    {savingTreatment ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><Save className="h-4 w-4 mr-2" />{editingTreatment ? 'Update' : 'Add'} Service</>}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AppBuilder;
